/* eslint-disable react/prop-types */
import { useState } from "react";
import {
  Button,
  CircularProgress,
  IconButton,
  Typography,
  Box,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Card,
  Divider,
  Chip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import Select from "react-select";
import { Select as MuiSelect } from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Category as CategoryIcon,
  InfoOutlined as InfoIcon,
  AssessmentOutlined as AssessmentIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  getProbabilityList,
  getSeverityPeople,
  getSeverityEnvironment,
  getSeverityAsset,
  getSeverityReputation,
  riskLevelColor,
} from "@/lib/listData";
import SquareMatrix from "@/pages/user/newptw/Matrix";

function AddHazardDialog({
  open,
  onClose,
  onSave,
  precautionOptions,
  precautionsLoading,
  categoriesLoading,
  isLoading,
  onShowPrecautionsPopup,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [hazardName, setHazardName] = useState("");
  const [likelihood, setLikelihood] = useState("");
  const [severityPeople, setSeverityPeople] = useState("");
  const [severityAsset, setSeverityAsset] = useState("");
  const [severityEnvironment, setSeverityEnvironment] = useState("");
  const [severityReputation, setSeverityReputation] = useState("");
  const [selectedPrecautions, setSelectedPrecautions] = useState([]);

  const handleClose = () => {
    setHazardName("");
    setLikelihood("");
    setSeverityPeople("");
    setSeverityAsset("");
    setSeverityEnvironment("");
    setSeverityReputation("");
    setSelectedPrecautions([]);
    onClose();
  };

  const handleSave = () => {
    if (
      !hazardName.trim() ||
      !likelihood ||
      !severityPeople ||
      !severityAsset ||
      !severityEnvironment ||
      !severityReputation
    ) {
      return;
    }

    const formattedData = {
      name: hazardName,
      precaution_ids: selectedPrecautions.map((prec) => prec.value),
      likelihood: parseInt(likelihood),
      severity_people: parseInt(severityPeople),
      severity_asset: parseInt(severityAsset),
      severity_environment: parseInt(severityEnvironment),
      severity_reputation: parseInt(severityReputation),
    };

    onSave(formattedData, () => {
      setHazardName("");
      setLikelihood("");
      setSeverityPeople("");
      setSeverityAsset("");
      setSeverityEnvironment("");
      setSeverityReputation("");
      setSelectedPrecautions([]);
    });
  };

  const getRiskLevelDisplayColor = (likelihood, severity) => {
    if (!likelihood || !severity) return null;
    const colorData = riskLevelColor[likelihood]?.[severity];
    return colorData?.backgroundColor || null;
  };

  const getHighestRiskLevel = () => {
    if (!likelihood) return null;

    const severityValues = [
      parseInt(severityPeople),
      parseInt(severityEnvironment),
      parseInt(severityAsset),
      parseInt(severityReputation),
    ].filter((val) => !isNaN(val));

    if (severityValues.length === 0) return null;

    const maxSeverity = Math.max(...severityValues);
    return maxSeverity;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: theme.palette.checklist.surface.subtle,
          borderBottom: 1,
          borderColor: theme.palette.checklist.border.subtle,
          pb: 1.5,
          pt: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "primary.contrastText",
            }}
          >
            <AddIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
              {t("hazards.addDialogTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("hazards.addDialogSubtitle")}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              bgcolor: theme.palette.checklist.interactive.hover,
              "&:hover": {
                bgcolor: theme.palette.checklist.interactive.selected,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          bgcolor: alpha(theme.palette.background.default, 0.4),
        }}
      >
        <Box sx={{ maxHeight: "80vh", overflowY: "auto" }}>
          <Stack spacing={3} sx={{ p: 3 }}>
            {/* Section 1: Basic Information */}
            <Card
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: theme.shadows[4],
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <InfoIcon sx={{ color: "primary.main", fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="600">
                      {t("hazards.basicInformation")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("hazards.basicInformationDesc")}
                    </Typography>
                  </Box>
                  <Chip
                    label="1"
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: "primary.main",
                      fontWeight: "bold",
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ p: 3 }}>
                <TextField
                  label={t("hazards.newHazardLabel")}
                  variant="outlined"
                  value={hazardName}
                  onChange={(e) => setHazardName(e.target.value)}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: theme.shadows[2],
                      },
                      "&.Mui-focused": {
                        boxShadow: theme.shadows[4],
                      },
                    },
                  }}
                  aria-label={t("hazards.newHazardAria")}
                />
              </Box>
            </Card>

            {/* Section 2: Precautions Selection */}
            <Card
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: theme.shadows[4],
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CategoryIcon
                      sx={{ color: "success.main", fontSize: 20 }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="600">
                      {t("hazards.precautionsSelection")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("hazards.precautionsSelectionDesc")}
                    </Typography>
                  </Box>
                  <Chip
                    label="2"
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: "success.main",
                      fontWeight: "bold",
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ p: 3 }}>
                <Stack spacing={2}>
                  {/* View Selected Button - Now at the top */}
                  <Button
                    variant="outlined"
                    onClick={() =>
                      onShowPrecautionsPopup(
                        selectedPrecautions,
                        setSelectedPrecautions
                      )
                    }
                    disabled={selectedPrecautions.length === 0}
                    startIcon={<CategoryIcon />}
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: "medium",
                      borderColor: theme.palette.success.main,
                      color: theme.palette.success.main,
                      "&:hover": {
                        borderColor: theme.palette.success.dark,
                        bgcolor: alpha(theme.palette.success.main, 0.04),
                      },
                    }}
                  >
                    {selectedPrecautions.length > 0
                      ? t("hazards.viewSelectedPrecautions", {
                          count: selectedPrecautions.length,
                        })
                      : t("hazards.noPrecautionsSelected")}
                  </Button>

                  {/* Dropdown - Now below the button */}
                  <Select
                    isMulti
                    value={selectedPrecautions}
                    onChange={setSelectedPrecautions}
                    options={precautionOptions}
                    placeholder={t("hazards.selectPrecautionsPlaceholder")}
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    isLoading={precautionsLoading || categoriesLoading}
                    closeMenuOnSelect={false}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: 8,
                        borderColor: theme.palette.divider,
                        minHeight: 48,
                        "&:hover": {
                          borderColor: theme.palette.success.main,
                        },
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected
                          ? theme.palette.success.main
                          : state.isFocused
                            ? alpha(theme.palette.success.main, 0.1)
                            : "white",
                        color: state.isSelected
                          ? "white"
                          : theme.palette.text.primary,
                      }),
                      groupHeading: (base) => ({
                        ...base,
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: theme.palette.success.main,
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        padding: "8px 12px",
                        margin: "4px 0",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }),
                      multiValue: (base) => ({
                        ...base,
                        display: "none",
                      }),
                    }}
                  />
                </Stack>
              </Box>
            </Card>

            {/* Section 3: Risk Assessment */}
            <Card
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: theme.shadows[4],
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.main, 0.02)} 100%)`,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AssessmentIcon
                      sx={{ color: "warning.main", fontSize: 20 }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="600">
                      {t("hazards.riskAssessmentTitle")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("hazards.riskAssessmentDesc")}
                    </Typography>
                  </Box>
                  <Chip
                    label="3"
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      color: "warning.main",
                      fontWeight: "bold",
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", gap: 3 }}>
                  {/* Left Column - Risk Inputs */}
                  <Box sx={{ flex: 1 }}>
                    <Stack spacing={2.5}>
                      {/* Likelihood */}
                      <FormControl
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      >
                        <InputLabel>{t("hazards.likelihoodLabel")}</InputLabel>
                        <MuiSelect
                          value={likelihood}
                          onChange={(e) => setLikelihood(e.target.value)}
                          label={t("hazards.likelihoodLabel")}
                        >
                          {getProbabilityList(t).map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </MuiSelect>
                      </FormControl>

                      <Divider sx={{ my: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight="600"
                        >
                          {t("hazards.severityLevels")}
                        </Typography>
                      </Divider>

                      {/* Severity People */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <FormControl
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        >
                          <InputLabel>
                            {t("hazards.severityPeopleLabel")}
                          </InputLabel>
                          <MuiSelect
                            value={severityPeople}
                            onChange={(e) => setSeverityPeople(e.target.value)}
                            label={t("hazards.severityPeopleLabel")}
                          >
                            {getSeverityPeople(t).map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </MuiSelect>
                        </FormControl>
                        {likelihood && severityPeople && (
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 1,
                              backgroundColor: getRiskLevelDisplayColor(
                                parseInt(likelihood),
                                parseInt(severityPeople)
                              ),
                              border: `2px solid ${
                                getRiskLevelDisplayColor(
                                  parseInt(likelihood),
                                  parseInt(severityPeople)
                                ) === "white"
                                  ? theme.palette.divider
                                  : "transparent"
                              }`,
                              flexShrink: 0,
                              boxShadow: theme.shadows[1],
                            }}
                          />
                        )}
                      </Box>

                      {/* Severity Environment */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <FormControl
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        >
                          <InputLabel>
                            {t("hazards.severityEnvironmentLabel")}
                          </InputLabel>
                          <MuiSelect
                            value={severityEnvironment}
                            onChange={(e) =>
                              setSeverityEnvironment(e.target.value)
                            }
                            label={t("hazards.severityEnvironmentLabel")}
                          >
                            {getSeverityEnvironment(t).map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </MuiSelect>
                        </FormControl>
                        {likelihood && severityEnvironment && (
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 1,
                              backgroundColor: getRiskLevelDisplayColor(
                                parseInt(likelihood),
                                parseInt(severityEnvironment)
                              ),
                              border: `2px solid ${
                                getRiskLevelDisplayColor(
                                  parseInt(likelihood),
                                  parseInt(severityEnvironment)
                                ) === "white"
                                  ? theme.palette.divider
                                  : "transparent"
                              }`,
                              flexShrink: 0,
                              boxShadow: theme.shadows[1],
                            }}
                          />
                        )}
                      </Box>

                      {/* Severity Asset */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <FormControl
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        >
                          <InputLabel>
                            {t("hazards.severityAssetLabel")}
                          </InputLabel>
                          <MuiSelect
                            value={severityAsset}
                            onChange={(e) => setSeverityAsset(e.target.value)}
                            label={t("hazards.severityAssetLabel")}
                          >
                            {getSeverityAsset(t).map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </MuiSelect>
                        </FormControl>
                        {likelihood && severityAsset && (
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 1,
                              backgroundColor: getRiskLevelDisplayColor(
                                parseInt(likelihood),
                                parseInt(severityAsset)
                              ),
                              border: `2px solid ${
                                getRiskLevelDisplayColor(
                                  parseInt(likelihood),
                                  parseInt(severityAsset)
                                ) === "white"
                                  ? theme.palette.divider
                                  : "transparent"
                              }`,
                              flexShrink: 0,
                              boxShadow: theme.shadows[1],
                            }}
                          />
                        )}
                      </Box>

                      {/* Severity Reputation */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <FormControl
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        >
                          <InputLabel>
                            {t("hazards.severityReputationLabel")}
                          </InputLabel>
                          <MuiSelect
                            value={severityReputation}
                            onChange={(e) =>
                              setSeverityReputation(e.target.value)
                            }
                            label={t("hazards.severityReputationLabel")}
                          >
                            {getSeverityReputation(t).map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </MuiSelect>
                        </FormControl>
                        {likelihood && severityReputation && (
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 1,
                              backgroundColor: getRiskLevelDisplayColor(
                                parseInt(likelihood),
                                parseInt(severityReputation)
                              ),
                              border: `2px solid ${
                                getRiskLevelDisplayColor(
                                  parseInt(likelihood),
                                  parseInt(severityReputation)
                                ) === "white"
                                  ? theme.palette.divider
                                  : "transparent"
                              }`,
                              flexShrink: 0,
                              boxShadow: theme.shadows[1],
                            }}
                          />
                        )}
                      </Box>
                    </Stack>
                  </Box>

                  {/* Right Column - Risk Matrix */}
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 2,
                      bgcolor: alpha(theme.palette.background.paper, 0.5),
                      borderRadius: 2,
                      border: `1px dashed ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        transform: "scale(1.3)",
                        transformOrigin: "center",
                      }}
                    >
                      <SquareMatrix
                        receivedProbability={
                          likelihood ? parseInt(likelihood) : null
                        }
                        receivedSeverity={getHighestRiskLevel()}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          p: 3,
          gap: 2,
          bgcolor: alpha(theme.palette.background.default, 0.4),
          borderTop: `1px solid ${theme.palette.divider}`,
          justifyContent: "space-between",
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {t("hazards.requiredFieldsNote")}
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            color="inherit"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.25,
              textTransform: "none",
              fontWeight: "medium",
              borderColor: theme.palette.divider,
              "&:hover": {
                borderColor: theme.palette.text.primary,
                bgcolor: alpha(theme.palette.text.primary, 0.04),
              },
            }}
          >
            {t("hazards.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={
              isLoading ||
              !hazardName.trim() ||
              !likelihood ||
              !severityPeople ||
              !severityAsset ||
              !severityEnvironment ||
              !severityReputation
            }
            startIcon={
              isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <AddIcon />
              )
            }
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.25,
              textTransform: "none",
              fontWeight: "bold",
              boxShadow: 3,
              "&:hover": {
                boxShadow: 6,
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                bgcolor: alpha(theme.palette.primary.main, 0.3),
              },
            }}
            aria-label={t("hazards.addButtonAria")}
          >
            {t("hazards.save")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default AddHazardDialog;
